<template>
  <Teleport to="body">
    <div class="booking-toast-host" aria-live="polite" aria-atomic="true">
      <TransitionGroup name="booking-toast" tag="div" class="booking-toast-host__list">
        <output
          v-for="toast in toasts"
          :key="toast.id"
          class="booking-toast"
          :class="`booking-toast--${toast.type}`"
        >
          <span class="booking-toast__icon" :class="iconClass(toast.type)" aria-hidden="true" />
          <span class="booking-toast__content">
            <span class="booking-toast__title">{{ toast.title }}</span>
            <span v-if="toast.text" class="booking-toast__text">{{ toast.text }}</span>
          </span>
          <button
            type="button"
            class="booking-toast__close"
            :aria-label="$t('common.close')"
            @click="toastStore.dismiss(toast.id)"
          >
            ×
          </button>
        </output>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import {
  useBookingToastStore,
  type BookingToastType,
} from "../../store/useBookingToastStore";

const toastStore = useBookingToastStore();
const { toasts } = storeToRefs(toastStore);

function iconClass(type: BookingToastType): string {
  const icons: Record<BookingToastType, string> = {
    success: "vtmx-checkbox-circle-line",
    error: "vtmx-error-warning-line",
    info: "vtmx-information-line",
    warning: "vtmx-alert-line",
  };
  return icons[type];
}
</script>

<style scoped>
.booking-toast-host {
  position: fixed;
  top: 5rem;
  right: 1rem;
  z-index: 10050;
  width: min(22rem, calc(100vw - 2rem));
  pointer-events: none;
}

.booking-toast-host__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.booking-toast {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0;
  padding: 0.875rem 1rem;
  border-radius: var(--booking-radius-md);
  box-shadow: 0 10px 24px rgb(0 0 0 / 14%);
  border: 1px solid transparent;
  pointer-events: auto;
}

.booking-toast__icon {
  font-size: 1.25rem;
  line-height: 1;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.booking-toast__content {
  display: block;
  flex: 1;
  min-width: 0;
}

.booking-toast__title {
  display: block;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
}

.booking-toast__text {
  display: block;
  margin: 0.25rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.35;
  opacity: 0.92;
}

.booking-toast__close {
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.75;
  font-size: 1.125rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.booking-toast__close:hover {
  opacity: 1;
}

.booking-toast--success {
  background: var(--vtmn-color_green-light-3);
  border-color: var(--vtmn-color_green-light-1);
  color: var(--vtmn-color_green-dark-2);
}

.booking-toast--error {
  background: var(--vtmn-color_red-light-2);
  border-color: var(--vtmn-color_red-light-1);
  color: var(--vtmn-color_danger);
}

.booking-toast--info {
  background: var(--vtmn-color_brand-digital-light-3);
  border-color: var(--vtmn-color_brand-digital-light-2);
  color: var(--vtmn-color_brand-digital-dark-2);
}

.booking-toast--warning {
  background: var(--vtmn-color_orange-light-2);
  border-color: var(--vtmn-color_orange-light-1);
  color: var(--vtmn-color_orange);
}

.booking-toast-enter-active,
.booking-toast-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.booking-toast-enter-from,
.booking-toast-leave-to {
  opacity: 0;
  transform: translateX(1.25rem);
}

.booking-toast-move {
  transition: transform 0.3s ease;
}
</style>
